import { PaginationMeta } from '@sr-sdks/api-sdk-axios';
import {
  type DataTableEmptyState,
  type DataTableSortState,
  DataTable as ShadcnDataTable,
} from '@sr/ui-library';
import { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { ensureStableSort } from '../../utils/helpers/sort';
import { AppLoader } from '../Loader';
import { DataTablePagination } from './DataTablePagination';

export type { DataTableEmptyState };

export interface AppDataTableProps<TData, TValue = unknown> {
  className?: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Falls back to a plain "No results found" message when omitted. */
  emptyState?: DataTableEmptyState;
  /** Rendered above the table — search inputs etc; owned entirely by the caller. */
  filterElement?: ReactNode;
  id?: string;
  /**
   * True while a subsequent page/sort fetch is in flight and rows from the
   * previous page are still on screen (e.g. TanStack's `isPlaceholderData`
   * with `placeholderData: keepPreviousData`, or `isFetching && !isLoading`).
   * Keeps rows rendered — no flash of empty content — but layers a loading
   * overlay over them and disables the pagination controls.
   */
  isFetchingPage?: boolean;
  /**
   * True first load only — drives the skeleton rows and the empty-state
   * gate. Unset (default false) preserves every existing caller's behaviour.
   */
  isLoading?: boolean;
  /**
   * When provided alongside `renderExpandedRow`, an extra sibling row is
   * rendered directly beneath any row for which this returns true —
   * containing whatever `renderExpandedRow` returns, spanning all columns.
   */
  isRowExpanded?: (row: TData) => boolean;
  /**
   * Called whenever the user changes page (via the footer pagination) or
   * clicks a sortable header. `sortBy` is the new `"field:asc"|"field:desc"`
   * string (stabilised via `ensureStableSort`) when triggered by a sort
   * click, or the current `sortBy` prop passed straight through on page
   * changes so callers don't lose sort context while paginating.
   */
  onPageRequest?: (page: number, sortBy?: string) => void;
  /** Makes rows activatable — see `DataTableProps.onRowClick`. */
  onRowClick?: (row: TData) => void;
  /** URL search param name this table's pagination reads/writes. */
  paginationKey?: string;
  paginationMeta?: PaginationMeta | null;
  /** Content rendered inside the expanded sibling row — see `isRowExpanded`. */
  renderExpandedRow?: (row: TData) => ReactNode;
  /**
   * Also renders pagination controls inline with `filterElement`, in addition
   * to the footer pagination below the table. Off by default — opt in per
   * table where the design calls for pagination at both top and bottom (e.g.
   * Title Statements).
   */
  showTopPagination?: boolean;
  /**
   * Current server-driven sort string (e.g. `"invoiceDate:desc"`), used only
   * to render header sort-direction indicators — this is NOT tanstack's
   * built-in client sort state, since sorting stays entirely server-driven.
   */
  sortBy?: string;
  /**
   * URL search param name this table's sort reads/writes. Defaults to
   * `sortBy`; tables sharing a page (tabs) pass a unique name so one tab's
   * sort does not leak into another.
   */
  sortParam?: string;
}

/** Parses the primary (first, comma-separated) `field:direction` segment. */
const parseSortState = (sortBy?: string): DataTableSortState | null => {
  const primarySort = sortBy?.split(',')[0];

  if (!primarySort) return null;

  const [key, direction] = primarySort.split(':');

  if (!key || (direction !== 'asc' && direction !== 'desc')) return null;

  return { direction, key };
};

export const AppDataTable = <TData, TValue = unknown>({
  className,
  columns,
  data,
  emptyState,
  filterElement,
  id,
  isFetchingPage,
  isLoading,
  isRowExpanded,
  onPageRequest,
  onRowClick,
  paginationKey = 'page',
  paginationMeta,
  renderExpandedRow,
  showTopPagination,
  sortBy,
  sortParam = 'sortBy',
}: AppDataTableProps<TData, TValue>) => {
  const [, setSearchParams] = useSearchParams();

  // Disables pagination (top + footer) for BOTH a first load (`isLoading`)
  // and an in-between page/sort fetch (`isFetchingPage`).
  const disableControls = isLoading || isFetchingPage;

  // Mirrors AppTableV2HeaderCell: this click handler is the single source of
  // truth for writing `sortParam` into the URL. Only fires on an explicit click —
  // never on mount/render — so opening a page never pollutes the URL with a
  // sort nobody chose.
  const handleSort = (sortKey: string) => {
    if (!onPageRequest) return;

    const primarySort = sortBy?.split(',')[0];
    const nextDirection = primarySort === `${sortKey}:asc` ? 'desc' : 'asc';
    const nextSortBy = ensureStableSort(`${sortKey}:${nextDirection}`);

    setSearchParams(
      (current) => {
        current.set(sortParam, nextSortBy);
        current.set(paginationKey, '1');

        return current;
      },
      { replace: true },
    );

    onPageRequest(1, nextSortBy);
  };

  /*
   * Deriving emptiness from `totalItems` alone silently disabled `emptyState`
   * for every unpaginated caller -- `undefined === 0` is false, so a table with
   * no rows rendered a bare header instead of its empty panel.
   *
   * The row-count fallback is gated on `onPageRequest` being absent, which is
   * what separates the two kinds of caller. A server-paginated table has no
   * meta until its first response lands, and on the failed path never gets one
   * -- without the gate it would assert "there is no data" over a request that
   * had not run yet, or had just errored.
   */
  const isEmpty =
    !isLoading &&
    (paginationMeta
      ? paginationMeta.totalItems === 0
      : !onPageRequest && data.length === 0);

  // Composed here (rather than in the ui-library's own DataTable) so
  // `showTopPagination` stays a per-caller opt-in: it wraps whatever
  // `filterElement` the caller passed, so ShadcnDataTable's toolbar row still
  // renders a single ReactNode and every other consumer is unaffected.
  const toolbar = (filterElement || showTopPagination) && (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      {filterElement}
      {showTopPagination && (
        <DataTablePagination
          className="w-auto"
          hideRange
          isLoading={disableControls}
          paginationKey={paginationKey}
          paginationMeta={paginationMeta}
          sortBy={sortBy}
          onPageRequest={onPageRequest}
        />
      )}
    </div>
  );

  return (
    <div id={id} className={twMerge('flex w-full flex-col gap-5', className)}>
      <div className="relative">
        <ShadcnDataTable
          columns={columns}
          data={data}
          emptyState={emptyState}
          filterElement={toolbar}
          isEmpty={isEmpty}
          isLoading={isLoading}
          isRowExpanded={isRowExpanded}
          renderExpandedRow={renderExpandedRow}
          sortState={parseSortState(sortBy)}
          onRowClick={onRowClick}
          onSortChange={handleSort}
        />
        {/*
         * First load renders skeleton rows via `isLoading` (unchanged); this
         * overlay is only for `isFetchingPage`, where real rows from the
         * previous page are already on screen and just need to read as
         * "busy" — same treatment AppTableV2 gives its overlay, and it
         * doubles as blocking clicks on the sortable headers underneath.
         */}
        {isFetchingPage && (
          <div className="absolute inset-0 z-0">
            <AppLoader.Overlay />
          </div>
        )}
      </div>

      <DataTablePagination
        isLoading={disableControls}
        paginationKey={paginationKey}
        paginationMeta={paginationMeta}
        sortBy={sortBy}
        onPageRequest={onPageRequest}
      />
    </div>
  );
};
